import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress, Tooltip, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';

// ── Colores por catálogo (barra lateral) ─────────────────────────────────────
const COLORES_CATALOGO = ['#34C759','#007AFF','#FF9500','#5E5CE6','#FF3B30','#5AC8FA','#FF2D55','#30D158'];
// Catálogo oficial: usa los combos del árbol; el resto captura manual
const CAT_CPMS_NOMBRE = 'Catálogo de Procedimientos Médicos y Sanitarios del Sector Salud';

// ── Helpers ───────────────────────────────────────────────────────────────────
const TH = ({ children, center, right, w }) => (
  <th style={{ padding:'9px 10px', width:w, textAlign:center?'center':right?'right':'left',
    fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em',
    textTransform:'uppercase', backgroundColor:IOS.surface,
    borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap',
    position:'sticky', top:0, zIndex:1 }}>{children}</th>
);

const Lbl = ({ children }) => (
  <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
    letterSpacing:'0.07em', textTransform:'uppercase', mb:0.5 }}>{children}</Typography>
);

const Inp = ({ value, onChange, placeholder, mono, type='text', readOnly, style={} }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly}
    style={{ width:'100%', height:34, padding:'0 10px', border:`1px solid ${IOS.separator}`,
      borderRadius:8, fontSize:'0.8rem', color:readOnly?IOS.label2:IOS.label,
      backgroundColor:readOnly?IOS.bg:IOS.surface, outline:'none', boxSizing:'border-box',
      fontFamily:mono?'monospace':'inherit', ...style }}
    onFocus={e=>{ if(!readOnly) e.target.style.borderColor=IOS.blue; }}
    onBlur={e=>{ e.target.style.borderColor=IOS.separator; }} />
);

// Sel custom con portal
const Sel = ({ value, onChange, children, placeholder='Seleccionar...' }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const opciones = useMemo(() => {
    const opts = [];
    React.Children.forEach(children, ch => {
      if (ch && ch.type === 'option') opts.push({ value: String(ch.props.value ?? ''), label: ch.props.children });
    });
    return opts;
  }, [children]);
  const selLabel = opciones.find(o => o.value === String(value ?? ''))?.label || '';

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <Box ref={ref} sx={{ position:'relative', width:'100%' }}>
      <Box onClick={() => { if(ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(v=>!v); }}
        sx={{ height:34, px:1.2, pr:3.5, display:'flex', alignItems:'center',
          border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:8,
          bgcolor:IOS.surface, cursor:'pointer', userSelect:'none', position:'relative',
          boxShadow:open?`0 0 0 3px ${IOS.blue}15`:'none', transition:'all 0.15s' }}>
        <Typography sx={{ fontSize:'0.8rem', color:selLabel?IOS.label:IOS.label2,
          flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selLabel || placeholder}
        </Typography>
        <Box sx={{ position:'absolute', right:9, top:'50%',
          transform:`translateY(-50%) rotate(${open?180:0}deg)`, transition:'transform 0.2s' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </Box>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box sx={{ position:'fixed', top:rect.bottom+2, left:rect.left, width:rect.width,
          zIndex:99999, bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`,
          borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.13)',
          maxHeight:220, overflowY:'auto',
          '&::-webkit-scrollbar':{width:3}, '&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4} }}>
          {opciones.map((op, i) => (
            <Box key={op.value}
              onMouseDown={e => { e.preventDefault(); onChange({ target:{value:op.value} }); setOpen(false); }}
              sx={{ px:1.5, py:0.9, cursor:'pointer', whiteSpace:'nowrap',
                bgcolor:String(value??'')===op.value?`${IOS.blue}08`:'transparent',
                borderBottom:i<opciones.length-1?`1px solid ${IOS.separator}`:'none',
                '&:hover':{ bgcolor:IOS.bg } }}>
              <Typography sx={{ fontSize:'0.8rem',
                color:String(value??'')===op.value?IOS.blue:IOS.label,
                fontWeight:String(value??'')===op.value?700:400 }}>{op.label}</Typography>
            </Box>
          ))}
        </Box>, document.body
      )}
    </Box>
  );
};
// ── Combo editable: escribe libre o elige una sugerencia (llena código+nombre) ──
const ComboEditable = ({ codigo, nombre, onCodigo, onNombre, onPick, sugerencias=[] }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const h = e => {
      const dentro = (ref.current && ref.current.contains(e.target)) ||
                     (popupRef.current && popupRef.current.contains(e.target));
      if (!dentro) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtradas = useMemo(() => {
    const t = (nombre || '').trim().toLowerCase();
    if (!t) return sugerencias;
    return sugerencias.filter(s =>
      s.nombre.toLowerCase().includes(t) || s.codigo.toLowerCase().includes(t));
  }, [sugerencias, nombre]);

  const abrir = () => {
    if (sugerencias.length === 0) return;
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(true);
  };
  const inputStyle = {
    height:34, padding:'0 10px', border:`1px solid ${IOS.separator}`,
    borderRadius:8, fontSize:'0.78rem', color:IOS.label, backgroundColor:IOS.surface,
    outline:'none', boxSizing:'border-box',
  };

  return (
    <Box ref={ref} sx={{ position:'relative', display:'flex', gap:0.8 }}>
      <input value={codigo} onChange={e=>onCodigo(e.target.value)} placeholder="Código"
        onFocus={e=>{ e.target.style.borderColor=IOS.blue; abrir(); }}
        onBlur={e=>e.target.style.borderColor=IOS.separator}
        style={{ ...inputStyle, width:100, flexShrink:0, fontFamily:'monospace' }} />
      <input value={nombre} onChange={e=>{ onNombre(e.target.value); abrir(); }} placeholder="Nombre"
        onFocus={e=>{ e.target.style.borderColor=IOS.blue; abrir(); }}
        onBlur={e=>e.target.style.borderColor=IOS.separator}
        style={{ ...inputStyle, flex:1, width:'100%', fontFamily:'inherit' }} />
      {open && rect && filtradas.length > 0 && ReactDOM.createPortal(
        <Box ref={popupRef} sx={{ position:'fixed', top:rect.bottom+2, left:rect.left, width:rect.width,
          zIndex:99999, bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`,
          borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.13)',
          maxHeight:200, overflowY:'auto',
          '&::-webkit-scrollbar':{width:3}, '&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4} }}>
          {filtradas.map((s, i) => (
            <Box key={`${s.codigo}-${i}`}
              onMouseDown={e => { e.preventDefault(); onPick(s); setOpen(false); }}
              sx={{ px:1.5, py:0.8, cursor:'pointer',
                borderBottom:i<filtradas.length-1?`1px solid ${IOS.separator}`:'none',
                '&:hover':{ bgcolor:IOS.bg } }}>
              <Typography sx={{ fontSize:'0.78rem', color:IOS.label }}>
                <span style={{ fontFamily:'monospace', fontWeight:700, color:IOS.blue }}>{s.codigo}</span> — {s.nombre}
              </Typography>
            </Box>
          ))}
        </Box>, document.body
      )}
    </Box>
  );
};
// ── Modal Catálogo (crear/editar) ─────────────────────────────────────────────
const ModalCatalogo = ({ open, onClose, item, onGuardar, guardando, msgErr }) => {
  const [form, setForm] = useState({ nombre:'', descripcion:'', estado:true, orden:'' });
  useEffect(() => {
    if (open) setForm({
      nombre:      item?.nombre || '',
      descripcion: item?.descripcion || '',
      estado:      item?.estado ?? true,
      orden:       item?.orden ?? '',
    });
  }, [open, item]);

  if (!open) return null;
  const isEdit = !!item;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:9999,
      display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:{ xs:'0px', sm:'20px' }, width:'100%', maxWidth:440, mx:{ xs:0, sm:2 },
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label }}>
            {isEdit ? 'Editar catálogo' : 'Nuevo catálogo'}
          </Typography>
          <Box component="button" onClick={onClose}
            sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>
        <Box sx={{ p:2.5, display:'flex', flexDirection:'column', gap:1.5 }}>
          <Box>
            <Lbl>Nombre *</Lbl>
            <Inp value={form.nombre} onChange={e=>setForm(f=>({...f,nombre:e.target.value}))} placeholder="Nombre del catálogo" />
          </Box>
          <Box>
            <Lbl>Descripción</Lbl>
            <textarea value={form.descripcion} onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))}
              placeholder="Descripción breve..."
              style={{ width:'100%', height:72, padding:'8px 10px', border:`1px solid ${IOS.separator}`,
                borderRadius:8, fontSize:'0.8rem', color:IOS.label, backgroundColor:IOS.surface,
                outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'none' }}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=IOS.separator} />
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1.5 }}>
            <Box>
              <Lbl>Orden</Lbl>
              <Inp type="number" value={form.orden} onChange={e=>setForm(f=>({...f,orden:e.target.value}))} placeholder="1,2,3..." />
            </Box>
            <Box>
              <Lbl>Estado</Lbl>
              <Box sx={{ display:'flex', gap:0.8 }}>
                {[{v:true,l:'Activo'},{v:false,l:'Inactivo'}].map(opt=>(
                  <Box key={String(opt.v)} onClick={()=>setForm(f=>({...f,estado:opt.v}))}
                    sx={{ flex:1, height:34, borderRadius:8, cursor:'pointer', display:'flex',
                      alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                      border:`1.5px solid ${form.estado===opt.v?(opt.v?IOS.green:IOS.red):IOS.separator}`,
                      bgcolor:form.estado===opt.v?(opt.v?`${IOS.green}10`:`${IOS.red}10`):IOS.bg }}>
                    <Typography sx={{ fontSize:'0.78rem', fontWeight:form.estado===opt.v?700:500,
                      color:form.estado===opt.v?(opt.v?IOS.green:IOS.red):IOS.label2 }}>{opt.l}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {msgErr && (
            <Box sx={{ p:1.2, borderRadius:'10px', bgcolor:'#FFF0EF', border:'1px solid #FFD4D1' }}>
              <Typography sx={{ fontSize:'0.78rem', color:IOS.red }}>{msgErr}</Typography>
            </Box>
          )}
          <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end', pt:0.5 }}>
            <Box component="button" onClick={onClose}
              sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
                bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
              Cancelar
            </Box>
            <Box component="button" onClick={()=>onGuardar(form)} disabled={guardando||!form.nombre.trim()}
              sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                bgcolor:guardando||!form.nombre.trim()?'#E5E5EA':IOS.blue,
                cursor:guardando||!form.nombre.trim()?'default':'pointer',
                display:'flex', alignItems:'center', gap:0.8 }}>
              {guardando && <CircularProgress size={12} sx={{ color:'white' }} />}
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:guardando||!form.nombre.trim()?'#8E8E93':'white' }}>
                {guardando?'Guardando...':isEdit?'Actualizar':'Crear catálogo'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal Procedimiento (crear/editar) ────────────────────────────────────────
const ModalProcedimiento = ({ open, onClose, item, catId, esCpms, procsExistentes = [], grupos, onGuardar, guardando, msgErr }) => {
  const [grupoSel,      setGrupoSel]      = useState('');
  const [seccionSel,    setSeccionSel]    = useState('');
  const [subseccionSel, setSubseccionSel] = useState('');
  const [codigo,        setCodigo]        = useState('');
  const [nombre,        setNombre]        = useState('');
  const [estado,        setEstado]        = useState(true);
  // Modo manual (catálogos que NO son el CPMS oficial)
  const [man, setMan] = useState({ gCod:'', gNom:'', sCod:'', sNom:'', ssCod:'', ssNom:'' });

  const grupoObj      = grupos.find(g => g.codigoGrupo === grupoSel);
  const seccionesDisp = grupoObj?.secciones || [];
  const seccionObj    = seccionesDisp.find(s => s.codigoSeccion === seccionSel);
  const subsDisp      = seccionObj?.subsecciones || [];

  useEffect(() => {
    if (!open) return;
    if (item) {
      setGrupoSel((item.codigoGrupo || '').trim());
      setSeccionSel((item.codigoSeccion || '').trim());
      setSubseccionSel((item.codigoSubseccion || '').trim());
      setCodigo(item.codigoProcedimiento || '');
      setNombre(item.nombreProcedimiento || '');
      setEstado(item.estado ?? true);
      setMan({
        gCod:(item.codigoGrupo||'').trim(),   gNom:(item.nombreGrupo||'').trim(),
        sCod:(item.codigoSeccion||'').trim(),  sNom:(item.nombreSeccion||'').trim(),
        ssCod:(item.codigoSubseccion||'').trim(), ssNom:(item.nombreSubseccion||'').trim(),
      });
    } else {
      setGrupoSel(''); setSeccionSel(''); setSubseccionSel('');
      setCodigo(''); setNombre(''); setEstado(true);
      setMan({ gCod:'', gNom:'', sCod:'', sNom:'', ssCod:'', ssNom:'' });
    }
  }, [open, item, grupos]);

  // Al cambiar grupo manualmente, limpiar sección y subsección
  const handleGrupo = e => {
    const nuevo = e.target.value;
    // Solo limpiar si el usuario realmente cambió (no es la carga inicial)
    if (nuevo !== grupoSel) {
      setGrupoSel(nuevo);
      setSeccionSel('');
      setSubseccionSel('');
    }
  };
  const handleSeccion = e => {
    const nuevo = e.target.value;
    if (nuevo !== seccionSel) {
      setSeccionSel(nuevo);
      setSubseccionSel('');
    }
  };

  // Obtener datos del subsección seleccionada
  const subsObj = subsDisp.find(ss => ss.codigoSubseccion === subseccionSel);
  // Sugerencias del modo manual: lo ya registrado en este catálogo, en cascada
  const sugGrupos = useMemo(() => {
    const m = new Map();
    procsExistentes.forEach(p => { const c=(p.codigoGrupo||'').trim();
      if(c && !m.has(c)) m.set(c, { codigo:c, nombre:(p.nombreGrupo||'').trim() }); });
    return [...m.values()];
  }, [procsExistentes]);
  const sugSecciones = useMemo(() => {
    const m = new Map();
    procsExistentes.filter(p => !man.gCod.trim() || (p.codigoGrupo||'').trim() === man.gCod.trim())
      .forEach(p => { const c=(p.codigoSeccion||'').trim();
        if(c && !m.has(c)) m.set(c, { codigo:c, nombre:(p.nombreSeccion||'').trim() }); });
    return [...m.values()];
  }, [procsExistentes, man.gCod]);
  const sugSubsecciones = useMemo(() => {
    const m = new Map();
    procsExistentes.filter(p => !man.sCod.trim() || (p.codigoSeccion||'').trim() === man.sCod.trim())
      .forEach(p => { const c=(p.codigoSubseccion||'').trim();
        if(c && !m.has(c)) m.set(c, { codigo:c, nombre:(p.nombreSubseccion||'').trim() }); });
    return [...m.values()];
  }, [procsExistentes, man.sCod]);

  const handleGuardar = () => {
    if (esCpms) {
      if (!subsObj) return;
      onGuardar({
        codigoGrupo:         grupoObj?.codigoGrupo || '',
        nombreGrupo:         grupoObj?.nombreGrupo || '',
        codigoSeccion:       seccionObj?.codigoSeccion || '',
        nombreSeccion:       seccionObj?.nombreSeccion || '',
        codigoSubseccion:    subsObj.codigoSubseccion,
        nombreSubseccion:    subsObj.nombreSubseccion,
        codigoProcedimiento: codigo.trim(),
        nombreProcedimiento: nombre.trim(),
        estado,
      });
    } else {
      onGuardar({
        codigoGrupo:         man.gCod.trim(),
        nombreGrupo:         man.gNom.trim(),
        codigoSeccion:       man.sCod.trim(),
        nombreSeccion:       man.sNom.trim(),
        codigoSubseccion:    man.ssCod.trim(),
        nombreSubseccion:    man.ssNom.trim(),
        codigoProcedimiento: codigo.trim(),
        nombreProcedimiento: nombre.trim(),
        estado,
      });
    }
  };

  const valido = esCpms
    ? (subseccionSel && codigo.trim() && nombre.trim())
    : (man.gCod.trim() && man.gNom.trim() && man.sCod.trim() && man.sNom.trim()
       && man.ssCod.trim() && man.ssNom.trim() && codigo.trim() && nombre.trim());
  if (!open) return null;

  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:9999,
      display:'flex', alignItems:{ xs:'flex-end', sm:'flex-start' }, justifyContent:'center',
      p:{ xs:0, sm:2 }, pt:{ xs:0, sm:'6vh' }, backdropFilter:'blur(4px)', overflowY:'auto' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:{ xs:'0px', sm:'20px' }, width:'100%', maxWidth:600, mx:{ xs:0, sm:2 },
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)', overflow:'auto', mb:{ xs:0, sm:4 },
        maxHeight:{ xs:'92vh', sm:'none' } }}>
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label }}>
            {item ? 'Editar procedimiento' : 'Nuevo procedimiento'}
          </Typography>
          <Box component="button" onClick={onClose}
            sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>
        <Box sx={{ p:2.5, display:'flex', flexDirection:'column', gap:1.5 }}>

          {/* Selects anidados */}
          <Box sx={{ p:1.8, bgcolor:IOS.bg, borderRadius:'12px', border:`1px solid ${IOS.separator}` }}>
            <Typography sx={{ fontSize:'0.62rem', fontWeight:700, color:IOS.label2,
              textTransform:'uppercase', letterSpacing:'0.07em', mb:1.2 }}>
              Clasificación
            </Typography>
            {esCpms ? (
            <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
              <Box>
                <Lbl>Grupo *</Lbl>
                <Sel value={grupoSel} onChange={handleGrupo} placeholder="Selecciona grupo...">
                  {grupos.map(g => <option key={g.codigoGrupo} value={g.codigoGrupo}>{g.codigoGrupo} — {g.nombreGrupo}</option>)}
                </Sel>
              </Box>
              <Box>
                <Lbl>Sección *</Lbl>
                <Sel value={seccionSel} onChange={handleSeccion} placeholder={grupoSel?'Selecciona sección...':'Primero selecciona grupo'}>
                  {seccionesDisp.map(s => <option key={s.codigoSeccion} value={s.codigoSeccion}>{s.codigoSeccion} — {s.nombreSeccion}</option>)}
                </Sel>
              </Box>
              <Box>
                <Lbl>Subsección *</Lbl>
                <Sel value={subseccionSel} onChange={e=>setSubseccionSel(e.target.value)} placeholder={seccionSel?'Selecciona subsección...':'Primero selecciona sección'}>
                  {subsDisp.map(ss => <option key={ss.codigoSubseccion} value={ss.codigoSubseccion}>{ss.codigoSubseccion} — {ss.nombreSubseccion}</option>)}
                </Sel>
              </Box>
            </Box>
            ) : (
            <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
              <Box>
                <Lbl>Grupo *</Lbl>
                <ComboEditable codigo={man.gCod} nombre={man.gNom}
                  onCodigo={v=>setMan(f=>({...f,gCod:v}))}
                  onNombre={v=>setMan(f=>({...f,gNom:v}))}
                  onPick={s=>setMan(f=>({...f,gCod:s.codigo,gNom:s.nombre,sCod:'',sNom:'',ssCod:'',ssNom:''}))}
                  sugerencias={sugGrupos} />
              </Box>
              <Box>
                <Lbl>Sección *</Lbl>
                <ComboEditable codigo={man.sCod} nombre={man.sNom}
                  onCodigo={v=>setMan(f=>({...f,sCod:v}))}
                  onNombre={v=>setMan(f=>({...f,sNom:v}))}
                  onPick={s=>setMan(f=>({...f,sCod:s.codigo,sNom:s.nombre,ssCod:'',ssNom:''}))}
                  sugerencias={sugSecciones} />
              </Box>
              <Box>
                <Lbl>Subsección *</Lbl>
                <ComboEditable codigo={man.ssCod} nombre={man.ssNom}
                  onCodigo={v=>setMan(f=>({...f,ssCod:v}))}
                  onNombre={v=>setMan(f=>({...f,ssNom:v}))}
                  onPick={s=>setMan(f=>({...f,ssCod:s.codigo,ssNom:s.nombre}))}
                  sugerencias={sugSubsecciones} />
              </Box>
            </Box>
            )}
          </Box>

          {/* Datos del procedimiento */}
          <Box sx={{ display:'grid', gridTemplateColumns:'140px 1fr', gap:1.5 }}>
            <Box>
              <Lbl>Código *</Lbl>
              <Inp value={codigo} onChange={e=>setCodigo(e.target.value)} placeholder="Ej: 1.01.01" mono />
            </Box>
            <Box>
              <Lbl>Estado</Lbl>
              <Box sx={{ display:'flex', gap:0.8 }}>
                {[{v:true,l:'Activo'},{v:false,l:'Inactivo'}].map(opt=>(
                  <Box key={String(opt.v)} onClick={()=>setEstado(opt.v)}
                    sx={{ flex:1, height:34, borderRadius:8, cursor:'pointer', display:'flex',
                      alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                      border:`1.5px solid ${estado===opt.v?(opt.v?IOS.green:IOS.red):IOS.separator}`,
                      bgcolor:estado===opt.v?(opt.v?`${IOS.green}10`:`${IOS.red}10`):IOS.bg }}>
                    <Typography sx={{ fontSize:'0.78rem', fontWeight:estado===opt.v?700:500,
                      color:estado===opt.v?(opt.v?IOS.green:IOS.red):IOS.label2 }}>{opt.l}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box>
            <Lbl>Nombre del procedimiento *</Lbl>
            <textarea value={nombre} onChange={e=>setNombre(e.target.value)}
              placeholder="Nombre completo del procedimiento..."
              style={{ width:'100%', height:80, padding:'8px 10px', border:`1px solid ${IOS.separator}`,
                borderRadius:8, fontSize:'0.8rem', color:IOS.label, backgroundColor:IOS.surface,
                outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'none' }}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=IOS.separator} />
          </Box>

          {msgErr && (
            <Box sx={{ p:1.2, borderRadius:'10px', bgcolor:'#FFF0EF', border:'1px solid #FFD4D1' }}>
              <Typography sx={{ fontSize:'0.78rem', color:IOS.red }}>{msgErr}</Typography>
            </Box>
          )}
          <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end' }}>
            <Box component="button" onClick={onClose}
              sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
                bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
              Cancelar
            </Box>
            <Box component="button" onClick={handleGuardar} disabled={guardando||!valido}
              sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                bgcolor:guardando||!valido?'#E5E5EA':IOS.blue,
                cursor:guardando||!valido?'default':'pointer',
                display:'flex', alignItems:'center', gap:0.8 }}>
              {guardando && <CircularProgress size={12} sx={{ color:'white' }} />}
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:guardando||!valido?'#8E8E93':'white' }}>
                {guardando?'Guardando...':item?'Actualizar':'Agregar'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal confirmar eliminar ───────────────────────────────────────────────────
const ModalConfirm = ({ open, onClose, onConfirm, texto, eliminando }) => {
  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:10000,
      display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:'20px', width:340, mx:2,
        boxShadow:'0 24px 60px rgba(0,0,0,0.2)', p:2.5 }}>
        <Box sx={{ width:46, height:46, borderRadius:'50%', bgcolor:'#FFF0EF',
          display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:1.8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </Box>
        <Typography sx={{ fontWeight:700, color:IOS.label, textAlign:'center', mb:0.5 }}>¿Confirmar acción?</Typography>
        <Typography sx={{ fontSize:'0.82rem', color:IOS.label2, textAlign:'center', mb:2.5 }}>{texto}</Typography>
        <Box sx={{ display:'flex', gap:1, justifyContent:'center' }}>
          <Box component="button" onClick={onClose}
            sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
              bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
            Cancelar
          </Box>
          <Box component="button" onClick={onConfirm} disabled={eliminando}
            sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
              bgcolor:eliminando?'#E5E5EA':IOS.red, cursor:eliminando?'default':'pointer',
              display:'flex', alignItems:'center', gap:0.8 }}>
            {eliminando && <CircularProgress size={12} sx={{ color:'white' }} />}
            <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:eliminando?'#8E8E93':'white' }}>
              {eliminando?'Procesando...':'Confirmar'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const Catalogos = () => {
  const navigate = useNavigate();
  const user     = authService.getCurrentUser();
  const ueId     = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const nf       = user?.nombreCompleto || user?.usuario || '';
  const esTOTAL  = (user?.privilegio || '').toUpperCase() === 'TOTAL';
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  // ── Vista: 'lista' | 'detalle' ───────────────────────────────────────────
  const [vista,      setVista]      = useState('lista');
  const [catActivo,  setCatActivo]  = useState(null); // catálogo seleccionado

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const [catalogos,   setCatalogos]   = useState([]);
  const [cargCat,     setCargCat]     = useState(false);
  const [modalCat,    setModalCat]    = useState(false);
  const [editCat,     setEditCat]     = useState(null);
  const [guardCat,    setGuardCat]    = useState(false);
  const [errCat,      setErrCat]      = useState('');
  const [confirmCat,  setConfirmCat]  = useState(null);
  const [elimCat,     setElimCat]     = useState(false);

  // ── Procedimientos ────────────────────────────────────────────────────────
  const [procs,       setProcs]       = useState([]);
  const [cargProc,    setCargProc]    = useState(false);
  const [busqueda,    setBusqueda]    = useState('');
  const [grupos,      setGrupos]      = useState([]);
  const [modalProc,   setModalProc]   = useState(false);
  const [editProc,    setEditProc]    = useState(null);
  const [guardProc,   setGuardProc]   = useState(false);
  const [errProc,     setErrProc]     = useState('');
  const [confirmProc, setConfirmProc] = useState(null);
  const [elimProc,    setElimProc]    = useState(false);
  const [page,        setPage]        = useState(0);
  const [rpp,         setRpp]         = useState(50);

  // ── Cargar catálogos ──────────────────────────────────────────────────────
  const cargarCatalogos = async () => {
    setCargCat(true);
    try {
      const r = await api.get('/caja/catalogos/listar', { params: { ueId } });
      if (r.data.success) setCatalogos(r.data.data || []);
    } catch(e) { console.error(e); } finally { setCargCat(false); }
  };

  useEffect(() => { if (ueId) cargarCatalogos(); }, [ueId]);

  // ── Cargar grupos (árbol) ─────────────────────────────────────────────────
  useEffect(() => {
    api.get('/caja/catalogos/grupos')
      .then(r => { if (r.data.success) setGrupos(r.data.data || []); })
      .catch(console.error);
  }, []);

  // ── Cargar procedimientos ─────────────────────────────────────────────────
  const cargarProcs = async (catId, q = '') => {
    setCargProc(true); setPage(0);
    try {
      const r = await api.get('/caja/catalogos/procedimientos', { params: { catId, busqueda: q || null } });
      if (r.data.success) setProcs(r.data.data || []);
    } catch(e) { console.error(e); } finally { setCargProc(false); }
  };

  // Al entrar al detalle
  const abrirCatalogo = (cat) => {
    setCatActivo(cat); setBusqueda(''); setProcs([]); setVista('detalle');
    cargarProcs(cat.catId);
  };

  // ── CRUD catálogo ─────────────────────────────────────────────────────────
  const guardarCatalogo = async (form) => {
    setGuardCat(true); setErrCat('');
    try {
      const r = await api.post('/caja/catalogos/crud', {
        accion:      editCat ? 'UPDATE' : 'INSERT',
        catId:       editCat?.catId || null,
        nombre:      form.nombre,
        descripcion: form.descripcion,
        estado:      form.estado,
        orden:       form.orden !== '' ? parseInt(form.orden) : null,
        ueId,
      });
      if (r.data.success) { setModalCat(false); setEditCat(null); cargarCatalogos(); }
      else setErrCat(r.data.message || 'Error al guardar');
    } catch(e) { setErrCat('Error al guardar'); } finally { setGuardCat(false); }
  };

  const eliminarCatalogo = async () => {
    if (!confirmCat) return; setElimCat(true);
    try {
      await api.post('/caja/catalogos/crud', { accion:'DELETE', catId:confirmCat.catId, nombre:'', descripcion:'', estado:false, ueId });
      setConfirmCat(null); cargarCatalogos();
      if (catActivo?.catId === confirmCat.catId) setVista('lista');
    } catch(e) { console.error(e); } finally { setElimCat(false); }
  };

  // ── CRUD procedimiento ────────────────────────────────────────────────────
  const guardarProc = async (form) => {
    setGuardProc(true); setErrProc('');
    try {
      const r = await api.post('/caja/catalogos/procedimientos/crud', {
        accion: editProc ? 'UPDATE' : 'INSERT',
        cpId:   editProc?.cpId || null,
        catId:  catActivo.catId,
        ...form,
      });
      if (r.data.success) { setModalProc(false); setEditProc(null); cargarProcs(catActivo.catId, busqueda); }
      else setErrProc(r.data.message || 'Error al guardar');
    } catch(e) { setErrProc('Error al guardar'); } finally { setGuardProc(false); }
  };

  const eliminarProc = async () => {
    if (!confirmProc) return; setElimProc(true);
    try {
      await api.post('/caja/catalogos/procedimientos/crud', {
        accion:'UPDATE', cpId:confirmProc.cpId, catId:catActivo.catId,
        codigoGrupo:confirmProc.codigoGrupo, nombreGrupo:confirmProc.nombreGrupo,
        codigoSeccion:confirmProc.codigoSeccion, nombreSeccion:confirmProc.nombreSeccion,
        codigoSubseccion:confirmProc.codigoSubseccion, nombreSubseccion:confirmProc.nombreSubseccion,
        codigoProcedimiento:confirmProc.codigoProcedimiento, nombreProcedimiento:confirmProc.nombreProcedimiento,
        estado:false,
      });
      setConfirmProc(null); cargarProcs(catActivo.catId, busqueda);
    } catch(e) { console.error(e); } finally { setElimProc(false); }
  };

  const procsFilt = useMemo(() => procs, [procs]); // ya filtrado desde el backend
  const procsPag  = procsFilt.slice(page * rpp, (page + 1) * rpp);

  // ── Color catálogo ────────────────────────────────────────────────────────
  const colorCat = (idx) => COLORES_CATALOGO[idx % COLORES_CATALOGO.length];

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
        <HeaderIOS
          titulo={vista === 'lista' ? 'Catálogos' : catActivo?.nombre || 'Procedimientos'}
          subtitulo={vista === 'lista' ? 'Caja' : 'Catálogos'}
          onHome={() => vista === 'lista' ? navigate('/caja') : setVista('lista')}
          onLogout={handleLogout}
          nombreCompleto={nf}
          avatarLetra={nf.charAt(0).toUpperCase()} />

        <Box sx={{ maxWidth:1400, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

          {/* ══ VISTA LISTA DE CATÁLOGOS ══ */}
          {vista === 'lista' && (
            <Box>
              {/* Header acciones */}
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2.5 }}>
                <Box>
                  <Typography sx={{ fontWeight:700, fontSize:'1rem', color:IOS.label }}>
                    Tarifarios disponibles
                  </Typography>
                  <Typography sx={{ fontSize:'0.75rem', color:IOS.label2 }}>
                    {catalogos.length} catálogo{catalogos.length !== 1 ? 's' : ''} registrado{catalogos.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                {esTOTAL && (
                  <Box component="button" onClick={() => { setEditCat(null); setErrCat(''); setModalCat(true); }}
                    sx={{ height:36, px:2, borderRadius:'10px', border:'none', bgcolor:IOS.blue,
                      cursor:'pointer', display:'flex', alignItems:'center', gap:0.8,
                      '&:hover':{ filter:'brightness(0.92)' } }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:'white' }}>Nuevo catálogo</Typography>
                  </Box>
                )}
              </Box>

              {cargCat
                ? <Box sx={{ py:10, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                : catalogos.length === 0
                  ? <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:12, textAlign:'center',
                      border:`1px solid ${IOS.separator}` }}>
                      <Typography sx={{ fontWeight:700, color:IOS.label, mb:0.5 }}>Sin catálogos</Typography>
                      <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Crea el primer catálogo de procedimientos</Typography>
                    </Box>
                  : <Box sx={{ display:'flex', flexDirection:'column', gap:1.2 }}>
                      {catalogos.map((cat, idx) => (
                        <Box key={cat.catId}
                          sx={{ bgcolor:IOS.surface, borderRadius:'14px',
                            border:`1px solid ${IOS.separator}`,
                            boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
                            display:'flex', alignItems:'stretch', overflow:'hidden',
                            transition:'box-shadow 0.15s', '&:hover':{ boxShadow:'0 3px 12px rgba(0,0,0,0.1)' } }}>
                          {/* Barra color izquierda */}
                          <Box sx={{ width:6, bgcolor:colorCat(idx), flexShrink:0 }} />

                          {/* Contenido principal */}
                          <Box sx={{ flex:1, px:2.5, py:1.8, display:'flex', alignItems:'center', gap:3, flexWrap:'wrap' }}>
                            {/* Nombre + descripción */}
                            <Box sx={{ flex:'1 1 220px', minWidth:0 }}>
                              <Typography sx={{ fontWeight:700, fontSize:'0.92rem', color:IOS.label, mb:0.2 }}>
                                {cat.nombre}
                              </Typography>
                              {cat.descripcion && (
                                <Typography sx={{ fontSize:'0.75rem', color:IOS.label2,
                                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {cat.descripcion}
                                </Typography>
                              )}
                            </Box>

                            {/* Info PDF */}
                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5, flexShrink:0 }}>
                              {cat.rutaPdf
                                ? <Box sx={{ width:38, height:38, borderRadius:'10px', bgcolor:'#FFF0EF',
                                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#FF3B30" opacity="0.15"/>
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#FF3B30" strokeWidth="1.5" fill="none"/>
                                      <polyline points="14 2 14 8 20 8" stroke="#FF3B30" strokeWidth="1.5" fill="none"/>
                                      <text x="7" y="17" style={{fontSize:'5px',fontWeight:'bold',fill:'#FF3B30'}}>PDF</text>
                                    </svg>
                                  </Box>
                                : <Box sx={{ width:38, height:38, borderRadius:'10px', bgcolor:IOS.bg,
                                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                                    border:`1px solid ${IOS.separator}` }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.label3} strokeWidth="1.5" strokeLinecap="round">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                  </Box>
                              }
                              <Box>
                                <Typography sx={{ fontSize:'0.75rem', fontWeight:600, color:IOS.label }}>
                                  {cat.nombrePdf || 'Sin información'}
                                </Typography>
                                <Typography sx={{ fontSize:'0.68rem', color:IOS.label2 }}>
                                  {cat.pesoPdf || '0 kb'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Estado */}
                            <Box sx={{ flexShrink:0 }}>
                              <Box sx={{ px:1.2, py:0.4, borderRadius:'20px', display:'inline-flex', alignItems:'center', gap:0.5,
                                bgcolor:cat.estado?'#EDFAF2':'#FFF0EF',
                                border:`1px solid ${cat.estado?'#B7EFD0':'#FFD4D1'}` }}>
                                <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:cat.estado?IOS.green:IOS.red }} />
                                <Typography sx={{ fontSize:'0.68rem', fontWeight:700,
                                  color:cat.estado?'#1A6B3C':'#C0392B' }}>
                                  {cat.estado ? 'ACTIVO' : 'INACTIVO'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Acciones */}
                            <Box sx={{ display:'flex', alignItems:'center', gap:0.8, flexShrink:0 }}>
                              {esTOTAL && (
                                <>
                                  <Tooltip title="Editar" placement="top">
                                    <Box onClick={e=>{ e.stopPropagation(); setEditCat(cat); setErrCat(''); setModalCat(true); }}
                                      sx={{ width:28, height:28, borderRadius:'50%', bgcolor:`${IOS.blue}12`,
                                        display:'inline-flex', alignItems:'center', justifyContent:'center',
                                        cursor:'pointer', '&:hover':{ bgcolor:`${IOS.blue}22` } }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title="Desactivar" placement="top">
                                    <Box onClick={e=>{ e.stopPropagation(); setConfirmCat(cat); }}
                                      sx={{ width:28, height:28, borderRadius:'50%', bgcolor:'#FFF0EF',
                                        display:'inline-flex', alignItems:'center', justifyContent:'center',
                                        cursor:'pointer', '&:hover':{ bgcolor:'#FFD4D1' } }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                                    </Box>
                                  </Tooltip>
                                </>
                              )}
                              {/* Flecha → ir al catálogo */}
                              <Box onClick={()=>abrirCatalogo(cat)}
                                sx={{ width:34, height:34, borderRadius:'50%', bgcolor:IOS.bg,
                                  border:`1px solid ${IOS.separator}`, display:'inline-flex',
                                  alignItems:'center', justifyContent:'center', cursor:'pointer',
                                  transition:'all 0.15s', '&:hover':{ bgcolor:colorCat(idx), border:'none' },
                                  '&:hover svg path':{ stroke:'white' } }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
              }
            </Box>
          )}

          {/* ══ VISTA DETALLE PROCEDIMIENTOS ══ */}
          {vista === 'detalle' && catActivo && (
            <Box>
              {/* Breadcrumb + info catálogo */}
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2, flexWrap:'wrap' }}>
                <Box component="button" onClick={() => setVista('lista')}
                  sx={{ height:30, px:1.5, borderRadius:'8px', border:`1px solid ${IOS.separator}`,
                    bgcolor:IOS.surface, cursor:'pointer', display:'flex', alignItems:'center', gap:0.5,
                    '&:hover':{ bgcolor:IOS.bg } }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  <Typography sx={{ fontSize:'0.75rem', color:IOS.label2 }}>Catálogos</Typography>
                </Box>
                <Typography sx={{ fontSize:'0.75rem', color:IOS.label3 }}>/</Typography>
                <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:IOS.label }}>{catActivo.nombre}</Typography>
              </Box>

              {/* Tabla procedimientos */}
              <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', border:`1px solid ${IOS.separator}`,
                boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
                {/* Header tabla */}
                <Box sx={{ px:2.5, py:1.5, borderBottom:`1px solid ${IOS.separator}`,
                  display:'flex', alignItems:'center', gap:1.5, flexWrap:'wrap' }}>
                  {/* Buscador */}
                  <Box sx={{ position:'relative', flex:1, minWidth:200 }}>
                    <Box sx={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)',
                      color:IOS.label2, pointerEvents:'none' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </Box>
                    <input placeholder="Buscar procedimiento..."
                      value={busqueda} onChange={e => setBusqueda(e.target.value)}
                      onKeyDown={e => { if(e.key==='Enter') cargarProcs(catActivo.catId, busqueda); }}
                      style={{ width:'100%', height:32, paddingLeft:26, paddingRight:8,
                        border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.76rem',
                        color:IOS.label, backgroundColor:IOS.bg, outline:'none',
                        fontFamily:'inherit', boxSizing:'border-box' }}
                      onFocus={e=>e.target.style.borderColor=IOS.blue}
                      onBlur={e=>e.target.style.borderColor=IOS.separator} />
                  </Box>
                  <Box component="button" onClick={() => cargarProcs(catActivo.catId, busqueda)}
                    sx={{ width:30, height:30, borderRadius:'50%', border:`1px solid ${IOS.separator}`,
                      bgcolor:IOS.bg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                      '&:hover':{ bgcolor:`${IOS.blue}10` } }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  </Box>
                  <Box sx={{ px:1, py:0.3, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.blue }}>
                      {procsFilt.length} procedimientos
                    </Typography>
                  </Box>
                  {esTOTAL && (
                    <Box component="button" onClick={() => { setEditProc(null); setErrProc(''); setModalProc(true); }}
                      sx={{ height:30, px:1.5, borderRadius:'8px', border:'none', bgcolor:IOS.blue,
                        cursor:'pointer', display:'flex', alignItems:'center', gap:0.6,
                        '&:hover':{ filter:'brightness(0.92)' } }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      <Typography sx={{ fontSize:'0.76rem', fontWeight:700, color:'white' }}>Agregar</Typography>
                    </Box>
                  )}
                </Box>

                {/* Tabla */}
                {cargProc
                  ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                  : <><Box sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 340px)',
                      '&::-webkit-scrollbar':{ width:4, height:4 },
                      '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem', minWidth:900 }}>
                        <thead><tr>
                          <TH w={90}>Grupo</TH>
                          <TH w={90}>Sección</TH>
                          <TH w={100}>Subsección</TH>
                          <TH w={100}>Código</TH>
                          <TH>Procedimiento</TH>
                          <TH center w={80}>Estado</TH>
                          {esTOTAL && <TH center w={80}>Acciones</TH>}
                        </tr></thead>
                        <tbody>
                          {procsPag.length === 0
                            ? <tr><td colSpan={esTOTAL?7:6} style={{ padding:'48px', textAlign:'center',
                                color:IOS.label2, fontSize:'0.82rem' }}>
                                {busqueda ? 'Sin resultados para la búsqueda' : 'Sin procedimientos en este catálogo'}
                              </td></tr>
                            : procsPag.map((p, i) => (
                              <tr key={p.cpId}
                                style={{ borderBottom:`1px solid ${IOS.separator}`,
                                  backgroundColor:IOS.surface, transition:'background 0.1s' }}
                                onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                <td style={{ padding:'7px 10px' }}>
                                  <Typography sx={{ fontSize:'0.65rem', fontWeight:700, color:IOS.blue, fontFamily:'monospace' }}>{p.codigoGrupo}</Typography>
                                  <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, lineHeight:1.2,
                                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:80 }}>{p.nombreGrupo}</Typography>
                                </td>
                                <td style={{ padding:'7px 10px' }}>
                                  <Typography sx={{ fontSize:'0.65rem', fontWeight:700, color:IOS.purple, fontFamily:'monospace' }}>{p.codigoSeccion}</Typography>
                                  <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, lineHeight:1.2,
                                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:80 }}>{p.nombreSeccion}</Typography>
                                </td>
                                <td style={{ padding:'7px 10px' }}>
                                  <Typography sx={{ fontSize:'0.65rem', fontWeight:700, color:IOS.orange, fontFamily:'monospace' }}>{p.codigoSubseccion}</Typography>
                                  <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, lineHeight:1.2,
                                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:90 }}>{p.nombreSubseccion}</Typography>
                                </td>
                                <td style={{ padding:'7px 10px', fontFamily:'monospace', fontWeight:700, fontSize:'0.75rem', color:IOS.label }}>{p.codigoProcedimiento}</td>
                                <td style={{ padding:'7px 10px', fontSize:'0.76rem', color:IOS.label }}>{p.nombreProcedimiento}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}>
                                  <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.4, px:0.7, py:0.2, borderRadius:'20px',
                                    bgcolor:p.estado?'#EDFAF2':'#FFF0EF',
                                    border:`1px solid ${p.estado?'#B7EFD0':'#FFD4D1'}` }}>
                                    <Box sx={{ width:4, height:4, borderRadius:'50%', bgcolor:p.estado?IOS.green:IOS.red }} />
                                    <Typography sx={{ fontSize:'0.58rem', fontWeight:700,
                                      color:p.estado?'#1A6B3C':'#C0392B' }}>
                                      {p.estado?'ACTIVO':'INACTIVO'}
                                    </Typography>
                                  </Box>
                                </td>
                                {esTOTAL && (
                                  <td style={{ padding:'6px 10px', textAlign:'center' }}>
                                    <Box sx={{ display:'flex', gap:0.6, justifyContent:'center' }}>
                                      <Tooltip title="Editar" placement="top">
                                        <Box onClick={() => { setEditProc(p); setErrProc(''); setModalProc(true); }}
                                          sx={{ width:26, height:26, borderRadius:'50%', bgcolor:`${IOS.blue}12`,
                                            display:'inline-flex', alignItems:'center', justifyContent:'center',
                                            cursor:'pointer', '&:hover':{ bgcolor:`${IOS.blue}22` } }}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </Box>
                                      </Tooltip>
                                      <Tooltip title="Desactivar" placement="top">
                                        <Box onClick={() => setConfirmProc(p)}
                                          sx={{ width:26, height:26, borderRadius:'50%', bgcolor:'#FFF0EF',
                                            display:'inline-flex', alignItems:'center', justifyContent:'center',
                                            cursor:'pointer', '&:hover':{ bgcolor:'#FFD4D1' } }}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </td>
                                )}
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </Box>

                    {/* Cards móviles */}
                    <Box sx={{ display:{ xs:'block', md:'none' }, px:1.5, py:1.5,
                      maxHeight:'calc(100vh - 320px)', overflowY:'auto' }}>
                      {procsPag.length === 0
                        ? <Typography sx={{ py:6, textAlign:'center', color:IOS.label2, fontSize:'0.82rem' }}>
                            {busqueda ? 'Sin resultados para la búsqueda' : 'Sin procedimientos en este catálogo'}
                          </Typography>
                        : procsPag.map(p => (
                          <Box key={p.cpId} sx={{ mb:1.2, p:1.5, borderRadius:'12px',
                            border:`1px solid ${IOS.separator}`, bgcolor:IOS.surface }}>
                            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:0.5 }}>
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.blue, fontFamily:'monospace' }}>
                                {p.codigoProcedimiento}
                              </Typography>
                              <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.4, px:0.7, py:0.2, borderRadius:'20px',
                                bgcolor:p.estado?'#EDFAF2':'#FFF0EF',
                                border:`1px solid ${p.estado?'#B7EFD0':'#FFD4D1'}` }}>
                                <Box sx={{ width:4, height:4, borderRadius:'50%', bgcolor:p.estado?IOS.green:IOS.red }} />
                                <Typography sx={{ fontSize:'0.58rem', fontWeight:700,
                                  color:p.estado?'#1A6B3C':'#C0392B' }}>{p.estado?'ACTIVO':'INACTIVO'}</Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize:'0.8rem', color:IOS.label, mb:0.5 }}>
                              {p.nombreProcedimiento}
                            </Typography>
                            <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, mb:esTOTAL?1:0 }}>
                              {p.codigoGrupo} {p.nombreGrupo} · {p.codigoSubseccion} {p.nombreSubseccion}
                            </Typography>
                            {esTOTAL && (
                              <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end' }}>
                                <Box component="button" onClick={() => { setEditProc(p); setErrProc(''); setModalProc(true); }}
                                  sx={{ height:28, px:1.5, borderRadius:'8px', border:'none', bgcolor:`${IOS.blue}12`, cursor:'pointer' }}>
                                  <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.blue }}>Editar</Typography>
                                </Box>
                                <Box component="button" onClick={() => setConfirmProc(p)}
                                  sx={{ height:28, px:1.5, borderRadius:'8px', border:'none', bgcolor:'#FFF0EF', cursor:'pointer' }}>
                                  <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.red }}>Desactivar</Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ))}
                    </Box></>
                }
                {/* Paginación */}
                {procsFilt.length > rpp && (
                  <Box sx={{ bgcolor:IOS.surface, borderTop:`1px solid ${IOS.separator}` }}>
                    <TablePagination component="div" count={procsFilt.length} page={page}
                      onPageChange={(_,p) => setPage(p)} rowsPerPage={rpp}
                      onRowsPerPageChange={e => { setRpp(parseInt(e.target.value,10)); setPage(0); }}
                      rowsPerPageOptions={[25,50,100,200]} labelRowsPerPage="Filas:"
                      labelDisplayedRows={({from,to,count}) => `${from}–${to} de ${count}`} />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Modales */}
      <ModalCatalogo
        open={modalCat} onClose={() => { setModalCat(false); setEditCat(null); }}
        item={editCat} onGuardar={guardarCatalogo} guardando={guardCat} msgErr={errCat} />

      <ModalProcedimiento
        open={modalProc} onClose={() => { setModalProc(false); setEditProc(null); }}
        item={editProc} catId={catActivo?.catId}
        esCpms={(catActivo?.nombre || '').trim() === CAT_CPMS_NOMBRE}
        procsExistentes={procs} grupos={grupos}
        onGuardar={guardarProc} guardando={guardProc} msgErr={errProc} />

      <ModalConfirm
        open={!!confirmCat} onClose={() => setConfirmCat(null)}
        onConfirm={eliminarCatalogo} eliminando={elimCat}
        texto={`¿Desactivar el catálogo "${confirmCat?.nombre}"?`} />

      <ModalConfirm
        open={!!confirmProc} onClose={() => setConfirmProc(null)}
        onConfirm={eliminarProc} eliminando={elimProc}
        texto={`¿Desactivar "${confirmProc?.nombreProcedimiento}"?`} />
    </>
  );
};

export default Catalogos;
